import { createFileRoute, Link, Outlet, useMatch } from '@tanstack/react-router';
import { Box, Typography, Grid, Paper, Chip, Stack } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { Section } from '../../components/ui/Section';
import { Hero } from '../../components/ui/Hero';
import { getResearchPosts } from '../../lib/research';
import { getTagColor, getTagBgColor } from '../../lib/tags';
import { useSEO, useBreadcrumbSchema, PageSEO } from '../../lib/seo';
import { BrandColors } from '@neumind/shared/branding';
import { usePostHog } from '../../components/widgets/PostHogProvider';

export const Route = createFileRoute('/resources/research')({
  component: ResearchPage,
});

function ResearchPage() {
  const posts = getResearchPosts();
  const slugMatch = useMatch({ from: '/resources/research/$slug', shouldThrow: false });
  const { capture } = usePostHog();

  useSEO(PageSEO.research);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Research', url: '/resources/research' }
  ]);

  const handlePostClick = (post: { slug: string; title: string; tags: string[]; author: string }) => {
    capture('research_post_clicked', {
      post_slug: post.slug,
      post_title: post.title,
      post_tags: post.tags,
      post_author: post.author,
    });
  };

  // If we're on a child route (research post), render the Outlet
  if (slugMatch) {
    return <Outlet />;
  }

  return (
    <>
      <Hero
        title="Research & Evidence"
        subtitle="The clinical evidence and research underpinning neumind's approach to neurorehabilitation."
        variant="gradient"
        showScrollIndicator
      />

      {/* Evidence-Based Section */}
      <Section>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" sx={{ mb: 3 }}>
              Evidence-Based Approach
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Neumind is built on established research in cognitive rehabilitation, external
              memory aids, and microprompting strategies. Our approach aligns with INCOG
              2023 guidelines and has been co-designed with NHS clinical teams.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<VerifiedIcon />}
                label="INCOG 2023 Aligned"
                variant="outlined"
              />
              <Chip
                icon={<VerifiedIcon />}
                label="NHS Co-Designed"
                variant="outlined"
              />
              <Chip
                icon={<VerifiedIcon />}
                label="GDPR Compliant"
                variant="outlined"
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(77, 101, 255, 0.08) 0%, rgba(14, 165, 223, 0.08) 100%)',
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    83%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Task completion rate
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    120k+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Prompts delivered
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    30min
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weekly admin savings
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      fontSize: '2.5rem',
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    600+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active users
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Section>

      {/* Research Papers */}
      <Section
        title="Featured Research"
        subtitle="Publications and resources that inform our approach."
        centered
        variant="gradient"
      >
        <Grid container spacing={3}>
          {posts.map((post) => (
            <Grid item xs={12} sm={6} md={4} key={post.slug}>
              <Link
                to="/resources/research/$slug"
                params={{ slug: post.slug }}
                style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                onClick={() => handlePostClick(post)}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 0,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(77, 101, 255, 0.1)',
                    },
                  }}
                >
                  {post.image && (
                    <Box
                      sx={{
                        height: 180,
                        overflow: 'hidden',
                        bgcolor: 'grey.100',
                      }}
                    >
                      <Box
                        component="img"
                        src={post.image}
                        alt={post.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  )}
                  <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                      {post.tags.slice(0, 2).map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          sx={{
                            bgcolor: getTagBgColor(tag),
                            color: getTagColor(tag),
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Stack>
                    <Typography variant="h4" sx={{ mb: 1.5, color: 'text.primary' }}>
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ flex: 1, mb: 2 }}
                    >
                      {post.excerpt}
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="caption" color="text.secondary">
                        {new Date(post.date).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {post.author}
                      </Typography>
                    </Stack>
                  </Box>
                </Paper>
              </Link>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* Who is this for */}
      <Section>
        <Typography variant="h2" sx={{ textAlign: 'center', mb: 6 }}>
          Who is this research for?
        </Typography>
        <Grid container spacing={3}>
          {[
            {
              icon: <LocalHospitalIcon sx={{ fontSize: 32 }} />,
              title: 'Clinicians',
              description: 'Evidence to support tool adoption and demonstrate clinical value',
              color: BrandColors.primary.main,
            },
            {
              icon: <PersonIcon sx={{ fontSize: 32 }} />,
              title: 'Clients',
              description: 'Understanding how the approach works and why it helps recovery',
              color: BrandColors.success.main,
            },
            {
              icon: <Diversity3Icon sx={{ fontSize: 32 }} />,
              title: 'Families',
              description: 'Learning about supported recovery and how to stay involved',
              color: BrandColors.info.main,
            },
            {
              icon: <VolunteerActivismIcon sx={{ fontSize: 32 }} />,
              title: 'Caregivers',
              description: 'Tools and strategies to help coordinate care day-to-day',
              color: BrandColors.warning.main,
            },
          ].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item.title}>
              <Box
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: item.color,
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${item.color}15`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    bgcolor: `${item.color}15`,
                    color: item.color,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>
    </>
  );
}
